import { ApolloClient } from '@apollo/client';
import { GATE_LIST_ITEM_QUERY } from './gates.gql';

interface TerminalTypeSummary {
	terminalTypeId?: number | null;
	name?: string | null;
}

interface FacilitySummary {
	facilityId?: string | null;
	name?: string | null;
}

export interface GateListRecord {
	terminalId: string;
	name?: string | null;
	ipAddress?: string | null;
	gateKey?: string | null;
	isActive: boolean;
	terminalTypeId?: number | null;
	terminalType?: TerminalTypeSummary | null;
	facility?: FacilitySummary | null;
}

interface GateListItemResponse {
	terminal?: GateListRecord | null;
}

export interface SelectGateAfterSaveOptions {
	gateId: string;
	client: ApolloClient<any>;
	currentGates: GateListRecord[];
	onDataUpdate: (updatedGates: GateListRecord[]) => void;
	onGateSelect: (gate: GateListRecord) => void;
}

/**
 * Fetches the latest gate data from the server and updates the list,
 * then selects and highlights the gate in the grid and details panel.
 * 
 * @param options Configuration options for selecting the gate
 * @returns Promise that resolves when the operation is complete
 */
export async function selectGateAfterSave(
	options: SelectGateAfterSaveOptions
): Promise<void> {
	const { gateId, client, currentGates, onDataUpdate, onGateSelect } = options;

	try {
		// Step 1: Fetch the latest gate data from the server
		const { data } = await client.query<GateListItemResponse>({
			query: GATE_LIST_ITEM_QUERY,
			variables: { id: gateId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedGate = data?.terminal;

		if (!fetchedGate) {
			console.warn(`Gate with ID ${gateId} not found after save`);
			return;
		}

		// Step 2: Check if gate exists in current list
		const existingGateIndex = currentGates.findIndex(
			g => g.terminalId === gateId
		);

		let updatedGates: GateListRecord[];

		if (existingGateIndex >= 0) {
			// Gate exists - replace with updated data
			updatedGates = [...currentGates];
			updatedGates[existingGateIndex] = fetchedGate;
		} else {
			// New gate - append to the list
			updatedGates = [...currentGates, fetchedGate];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedGates);

		// Step 4: Select the gate (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onGateSelect(fetchedGate);
		}, 50);
	} catch (error) {
		console.error('Error selecting gate after save:', error);
		throw error;
	}
}
